import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Grid,
  Paper,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import ReportDataService from '../../../services/report.service';
import { useTranslation } from '../../../hooks/useTranslation';

const CHART_LABEL_KEYS = {
  confirmed_showed_up: 'reports.clinicConfirmedShowedUp',
  confirmed_no_show: 'reports.clinicConfirmedNoShow',
  confirmed_upcoming: 'reports.clinicConfirmedUpcoming',
  cancelled: 'reports.clinicCancelled',
  pending: 'reports.clinicPending',
};

const ClinicAppointmentBookingsReport = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [startDate, setStartDate] = useState(dayjs().subtract(30, 'day'));
  const [endDate, setEndDate] = useState(dayjs());

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (startDate) params.start_date = startDate.format('YYYY-MM-DD');
      if (endDate) params.end_date = endDate.format('YYYY-MM-DD');
      const res = await ReportDataService.getClinicAppointmentBookings(params);
      setData(res.data?.data || null);
    } catch (e) {
      setError(e.response?.data?.message || t('reports.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const summary = data?.summary || {};
  const chartItems = useMemo(() => {
    const items = (data?.chart || []).map((item) => ({
      ...item,
      label: t(CHART_LABEL_KEYS[item.key] || item.key),
    }));
    return items.filter((item) => item.count > 0);
  }, [data, t]);

  const maxChartCount = useMemo(
    () => Math.max(1, ...chartItems.map((item) => item.count)),
    [chartItems]
  );

  const statCards = [
    {
      label: t('reports.clinicTotalBookings'),
      value: summary.total_bookings ?? 0,
      color: '#37474f',
    },
    {
      label: t('reports.clinicConfirmedShowedUp'),
      value: summary.confirmed_showed_up ?? 0,
      color: '#43a047',
    },
    {
      label: t('reports.clinicConfirmedNoShow'),
      value: summary.confirmed_no_show ?? 0,
      color: '#fb8c00',
    },
    {
      label: t('reports.clinicCancelled'),
      value: summary.cancelled ?? 0,
      color: '#e53935',
    },
  ];

  return (
    <Box>
      <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
        {t('reports.clinicAppointmentBookings')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('reports.clinicAppointmentBookingsDescription')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <DatePicker
                label={t('reports.startDate')}
                value={startDate}
                onChange={(v) => setStartDate(v)}
              />
              <DatePicker
                label={t('reports.endDate')}
                value={endDate}
                onChange={(v) => setEndDate(v)}
              />
              <Button variant="contained" onClick={load} disabled={loading}>
                {t('reports.filter')}
              </Button>
            </Box>
          </LocalizationProvider>

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {statCards.map((card) => (
                  <Grid item xs={12} sm={6} md={3} key={card.label}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderLeft: 4,
                        borderColor: card.color,
                        height: '100%',
                      }}
                    >
                      <Typography variant="h4" fontWeight={800} sx={{ color: card.color }}>
                        {card.value}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {card.label}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {(summary.pending > 0 || summary.confirmed_upcoming > 0) && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {summary.pending > 0 &&
                    t('reports.clinicPendingCount', { count: summary.pending })}
                  {summary.pending > 0 && summary.confirmed_upcoming > 0 ? ' · ' : ''}
                  {summary.confirmed_upcoming > 0 &&
                    t('reports.clinicUpcomingCount', { count: summary.confirmed_upcoming })}
                </Typography>
              )}

              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                {t('reports.clinicChartTitle')}
              </Typography>

              {chartItems.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('reports.noData')}
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {chartItems.map((item) => (
                    <Box key={item.key}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          {item.label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.count}
                          {summary.total_bookings > 0 && (
                            <> ({Math.round((item.count / summary.total_bookings) * 100)}%)</>
                          )}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          height: 28,
                          bgcolor: 'grey.100',
                          borderRadius: 1,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            height: '100%',
                            width: `${(item.count / maxChartCount) * 100}%`,
                            bgcolor: item.color,
                            borderRadius: 1,
                            minWidth: item.count > 0 ? 8 : 0,
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ClinicAppointmentBookingsReport;
